import {useState, useContext, Fragment, useEffect} from 'react'
import classnames from 'classnames'
import Avatar from '@components/avatar'
import {useDispatch, useSelector} from 'react-redux'
import {useForm} from 'react-hook-form'
import {toast, Slide} from 'react-toastify'
import {handleLogin} from '@store/actions/auth'
import {AbilityContext} from '@src/utility/context/Can'
import {Link, useHistory} from 'react-router-dom'
import InputPasswordToggle from '@components/input-password-toggle'
import {getHomeRouteForLoggedInUser, isObjEmpty} from '@utils'
import {Coffee} from 'react-feather'
import {Formik} from "formik"
import * as yup from "yup"
import {
    Row,
    Col,
    CardTitle,
    CardText,
    Form,
    Input,
    FormGroup,
    Label,
    Button,
    Spinner
} from 'reactstrap'
import logo from '@src/assets/images/logo.png'
import '@styles/base/pages/page-auth.scss'
import queryString from "query-string"
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {PropTypes} from "prop-types"

const ErrorToastContent = ({msg}) => (
    <Fragment>
        <div className='toastify-header'>
            <div className='title-wrapper'>
                <Avatar size='sm' color='error' icon={<Coffee size={12}/>}/>
                <h6 className='toast-title font-weight-bold'>Opps</h6>
            </div>
        </div>
        <div className='toastify-body'>
            <span className="text-danger"><strong>{msg}</strong></span>
        </div>
    </Fragment>
)

ErrorToastContent.propTypes = {
    msg: PropTypes.string.isRequired
}

const Login = () => {
    const {executeRecaptcha} = useGoogleReCaptcha()
    const [isLoading, setIsLoading] = useState(false)
    const [captchaValue, setCaptchaValue] = useState(null)
    const [captchaError, setCaptchaError] = useState(false)
    const [queryStatus, setQueryStatus] = useState('')
    const [target, setTarget] = useState('other')
    const {errors} = useForm()
    const ability = useContext(AbilityContext)
    const dispatch = useDispatch()
    const history = useHistory()
    const authStoreSlice = useSelector(store => store.auth)
    const isIPhone = /iPhone|iPod/.test(navigator.userAgent)

    const sweetAlert = withReactContent(Swal)

    useEffect(() => {
        const loginError = authStoreSlice?.loginError || null
        if (loginError) {
            setIsLoading(false)
            setCaptchaError(false)
            setCaptchaValue(null)
            sweetAlert.fire({
                title: 'Network Error',
                icon: 'error',
                html: '<p>It seems there’s an issue with the network. Kindly refresh the page and try again.</p>',
                confirmButtonText: 'Reload',
                allowOutsideClick: false
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.reload()
                }
            })
            dispatch({type: 'LOGIN_ERROR', data: null})
        }

        const handleRedirect = (url, role, defaultPath) => {
            if (url?.role === role) {
                localStorage.removeItem('redirect_url')
                window.location.href = url.path
            } else {
                history.push(defaultPath)
            }
        }
        const userData = authStoreSlice?.userData || null
        if (userData) {
            setIsLoading(false)
            setCaptchaError(false)
            setCaptchaValue(null)
            if (userData.success === true) {
                ability.update([{action: "manage", subject: userData.role}])
                const redirectUrl = JSON.parse(localStorage.getItem('redirect_url'))
                if (userData.role === 'brand') {
                    if (userData.subscription_exp) {
                        history.push('/subscription-plans')
                    } else {
                        handleRedirect(redirectUrl, 'brand', '/dashboard/brands')
                    }
                } else if (userData.role === 'rd') {
                    if (!userData.profile_approved) {
                        history.push('/pending-account-msg')
                    } else {
                        handleRedirect(redirectUrl, 'rd', '/dashboard/rd')
                    }
                } else {
                    ability.update([{action: "manage", subject: "all"}])
                    history.push(getHomeRouteForLoggedInUser(userData.role))
                }
            }
            if (userData.success === false) {
                toast.error(
                    <ErrorToastContent
                        msg={userData.msg}
                        name={userData.name || userData.email}
                        role={userData.role}
                    />,
                    {transition: Slide, hideProgressBar: true, autoClose: 5000}
                )
            }
            dispatch({type: 'LOGIN', data: null})
        }
    }, [authStoreSlice])

    useEffect(() => {
        const parsed = queryString.parseUrl(window.location.href)
        setQueryStatus(parsed?.query?.session)
        setTarget(parsed?.query?.target || 'other')
        // Defer URL cleanup to allow third-party history listeners to initialize
        // Some analytics/affiliate scripts may reference internal fields (e.g., 'hpm')
        // during pushState and can throw if they run too early.
        try {
            setTimeout(() => {
                if (window.history && typeof window.history.pushState === 'function') {
                    window.history.pushState(null, '', '/login')
                }
            }, 0)
        } catch (e) {
            // no-op: avoid breaking login if a third-party listener misbehaves
        }
    }, [])

    const onSubmit = async (data) => {
        if (typeof executeRecaptcha !== 'function') {
            return
        }
        setIsLoading(true)
        const token = await executeRecaptcha('login')
        if (token) {
            dispatch(handleLogin({...data, target, re_captcha_token: token}))
        } else {
            sweetAlert.fire({
                title: 'Captcha Error',
                icon: 'error',
                html: '<p>Execute recaptcha not yet available.</p>',
                confirmButtonText: 'Reload',
                allowOutsideClick: false
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.reload()
                }
            })
        }
    }

    const handleClick = (e) => {
        e.target.removeAttribute('readonly')
        setTimeout(() => {
            const event = new Event('touchstart')
            e.target.dispatchEvent(event)
            e.target.focus()
        }, 0)
    }

    return (
        <div className='auth-wrapper auth-v2'>
            <Row className='auth-inner m-0'>
                <a
                    className="brand-logo"
                    href="https://www.therdlink.com/"
                    target="_self"
                    rel="noopener noreferrer"
                >
                    <img src={logo} width="140" alt='logo'/>
                </a>
                <Col className='d-flex align-items-center auth-bg px-2 p-lg-5' sm='12'>
                    <Col className='px-xl-2 mx-auto bg-light p-3 rounded' sm='8' md='6' lg='4'>
                        <CardTitle tag='h2' className='font-weight-bold mb-1'>
                            Welcome to The RD Link! 👋
                        </CardTitle>
                        {queryStatus && (
                            <CardText className='mb-2 bg-danger text-white p-1'>Your session has expired please login again.</CardText>
                        )}
                        <CardText className='mb-2'>Please sign-in to your account to pick up where you left off.</CardText>
                        <Formik
                            initialValues={{
                                email: "",
                                password: ""
                            }}
                            validationSchema={yup.object().shape({
                                email: yup.string().email().required("This field is required"),
                                password: yup.string().required("This field is required")
                            })}
                            onSubmit={(values, form) => {
                                onSubmit(values)
                            }}
                        >
                            {({
                                  errors,
                                  handleBlur,
                                  handleChange,
                                  handleSubmit,
                                  touched,
                                  values
                              }) => (
                                <Form className='auth-reset-password-form mt-2' onSubmit={handleSubmit}>
                                    <div className='form-group form-password-toggle'>
                                        <Label className={classnames({
                                            'is-invalid': touched.email && errors.email,
                                            'form-label': true
                                        })} htmlFor={`email`}>
                                            Email<span className="is-invalid">*</span>
                                        </Label>
                                        <Input
                                            type='email'
                                            name='email'
                                            placeholder='john@example.com'
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.email}
                                            id={`email`}
                                            className={classnames({'is-invalid': touched.email && errors.email})}
                                            readOnly={!isIPhone}
                                            onClick={(e) => handleClick(e)}
                                        />
                                        <div className="validation-err">{touched.email && errors.email}</div>
                                    </div>
                                    <FormGroup>
                                        <Label className={classnames({
                                            'is-invalid': touched.password && errors.password,
                                            'form-label': true
                                        })} htmlFor='password'>
                                            Password<span className="is-invalid">*</span>
                                        </Label>

                                        <InputPasswordToggle
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.password}
                                            name={`password`}
                                            id={`password`}
                                            className={classnames({'is-invalid': touched.password && errors.password})}
                                            readOnly={!isIPhone}
                                            onClick={(e) => handleClick(e)}
                                        />
                                        <div className="validation-err">{touched.password && errors.password}</div>
                                    </FormGroup>
                                    <Button.Ripple type='submit' disabled={isLoading || (typeof executeRecaptcha !== 'function')} color='primary' block>
                                        {isLoading ? (<Spinner style={{width: '20px', height: '20px'}}/>) : 'Sign in'}
                                    </Button.Ripple>
                                </Form>)}
                        </Formik>
                        <p className='text-center mt-2'>
                            <Link to='/forgot-password'>
                                <span>Forgot Password?</span>
                            </Link>
                        </p>
                        <p className='text-center mt-2'>
                            <span className='mr-25'>New on our platform?</span>
                            <Link to='/createloginrd'>
                                <span>Create an account</span>
                            </Link>
                        </p>
                    </Col>
                </Col>
            </Row>
        </div>
    )
}

export default Login
